import { useState } from "react";
import { format, formatDistanceToNow } from "date-fns";
import { Pencil, Play, StopCircle, Upload, MessageSquare, Sparkles as SparklesIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import type { ActivityLogEntry, ActivityLogEntryType } from "@/types/ab-tests";
import { useAddActivityLogComment } from "@/resources/ab-test/ab-test.api";

const ACTIVITY_ICON: Record<ActivityLogEntryType, React.ComponentType<{ className?: string }>> = {
  created: SparklesIcon,
  started: Play,
  stopped: StopCircle,
  edited: Pencil,
  "results-uploaded": Upload,
  comment: MessageSquare,
};

function ActivityIcon({ type }: { type: ActivityLogEntryType }) {
  const Icon = ACTIVITY_ICON[type];
  return <Icon className="h-3.5 w-3.5" />;
}

function formatTimestamp(iso: string) {
  const d = new Date(iso);
  return {
    absolute: format(d, "MMM d, yyyy 'at' h:mm a"),
    relative: formatDistanceToNow(d, { addSuffix: true }),
  };
}

interface ActivityLogTimelineProps {
  entries: ActivityLogEntry[];
  showHeader?: boolean;
  testId?: string;
  productId?: number;
  brandId?: number;
}

export function ActivityLogTimeline({ entries, showHeader = true, testId, productId, brandId }: ActivityLogTimelineProps) {
  const [commentDraft, setCommentDraft] = useState("");
  const [composerOpen, setComposerOpen] = useState(false);
  const addComment = useAddActivityLogComment();

  const handlePostComment = () => {
    if (!testId || !productId || !brandId) return;
    const text = commentDraft.trim();
    if (!text) return;
    addComment.mutate(
      { productId, testId, brandId, message: "Comment added", details: text },
      {
        onSuccess: () => {
          setCommentDraft("");
          setComposerOpen(false);
          toast.success("Comment posted");
        },
      },
    );
  };

  if (entries.length === 0 && !testId) return null;
  return (
    <div className="space-y-3">
      {(showHeader || testId) && (
        <div className="flex items-center justify-between gap-2 pt-4">
          {showHeader ? (
            <h3 className="text-xl font-semibold text-foreground">Activity</h3>
          ) : <span />}
          {testId && !composerOpen && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setComposerOpen(true)}
              className="gap-1.5"
            >
              <MessageSquare className="h-3.5 w-3.5" />
              Add a comment
            </Button>
          )}
        </div>
      )}

      {testId && composerOpen && (
        <div className="space-y-2">
          <Textarea
            placeholder="Add a comment to the test history..."
            value={commentDraft}
            onChange={(e) => setCommentDraft(e.target.value)}
            rows={2}
            autoFocus
            className="resize-none text-sm"
          />
          <div className="flex justify-end gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => { setCommentDraft(""); setComposerOpen(false); }}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handlePostComment}
              disabled={!commentDraft.trim() || addComment.isPending}
              className="gap-1.5"
            >
              <MessageSquare className="h-3.5 w-3.5" />
              {addComment.isPending ? "Posting..." : "Post comment"}
            </Button>
          </div>
        </div>
      )}
      <ol className="space-y-3">
        {entries.map((entry, idx) => {
          const ts = formatTimestamp(entry.timestamp);
          const isLast = idx === entries.length - 1;
          return (
            <li key={entry.id} className="relative flex gap-3">
              <div className="flex flex-col items-center">
                <div
                  className={cn(
                    "h-6 w-6 rounded-full border flex items-center justify-center shrink-0",
                    entry.type === "comment"
                      ? "bg-primary/10 border-primary/30 text-primary"
                      : entry.type === "started"
                      ? "bg-status-success-soft border-transparent text-foreground"
                      : entry.type === "stopped"
                      ? "bg-status-warning-soft border-transparent text-foreground"
                      : "bg-muted border-border text-muted-foreground",
                  )}
                >
                  <ActivityIcon type={entry.type} />
                </div>
                {!isLast && <div className="w-px flex-1 bg-border mt-1" />}
              </div>
              <div className="flex-1 min-w-0 pb-1">
                <p className="text-sm font-semibold text-foreground break-words">{entry.message}</p>
                {entry.details && (
                  <p
                    className={cn(
                      "text-sm leading-relaxed mt-0.5 break-words",
                      entry.type === "comment" ? "text-foreground" : "text-muted-foreground",
                    )}
                  >
                    {entry.details}
                  </p>
                )}
                <p className="text-xs text-muted-foreground mt-1" title={ts.absolute}>
                  {ts.relative}{entry.userName ? ` · ${entry.userName}` : ""}
                </p>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
